/**
 * useUnitMode — per-group ft/in <-> decimal preference, persisted to localStorage.
 *
 * Each dimension *group* (e.g. the Length/Width/Height row, one wall, one
 * ceiling zone) owns its own toggle and remembers its own mode independently,
 * keyed by a stable string. Pass a key that is stable for the group across
 * renders (prefer entity ids like wallId/zoneId over array indices so the
 * preference follows the entity if the list reorders).
 *
 * If product later decides on a single global/session preference instead, this
 * is the one place to swap: read/write a shared key (or a context value) and
 * every group follows it — no call-site changes needed.
 */

import { useCallback, useState } from 'react';
import { UnitMode } from '../utils/dimensions';

const STORAGE_PREFIX = 'trusscore.unitMode.';

function read(key: string, fallback: UnitMode): UnitMode {
  try {
    const saved = localStorage.getItem(STORAGE_PREFIX + key);
    return saved === 'decimal' || saved === 'ftin' ? saved : fallback;
  } catch {
    return fallback;
  }
}

export function useUnitMode(
  key: string,
  fallback: UnitMode = 'ftin',
): [UnitMode, (mode: UnitMode) => void] {
  const [mode, setModeState] = useState<UnitMode>(() => read(key, fallback));

  const setMode = useCallback(
    (next: UnitMode) => {
      setModeState(next);
      try {
        localStorage.setItem(STORAGE_PREFIX + key, next);
      } catch {
        /* ignore storage failures (private mode, quota, etc.) */
      }
    },
    [key],
  );

  return [mode, setMode];
}
